import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAcceptJobCard } from "../../../hooks";
import RejectServiceOrderModal from "../../completed/view/rejectServiceOrderModal";
import {
  DollarSign,
  User,
  Gauge,
  Users,
  PenTool,
  XCircle,
  CheckCircle2,
  Paperclip,
  Activity,
  Building, // For Facility
  Wifi, // For Network
  Server, // For System
  Box, // For Asset
} from "lucide-react";
import { Button } from "@shared/ui/button";
import { wxUtils } from "@features/wx/utils";

export const Overview = ({
  jobCard,
  signatureReportData,
}: {
  jobCard?: any;
  signatureReportData?: any;
}) => {
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const acceptJobCard = useAcceptJobCard();
  const handleApprove = () => {
    if (!jobCard?.id) return;
    acceptJobCard
      .mutateAsync({
        jobCardId: parseInt(jobCard.id),
      })
      .then(() => {
        navigate("/service-orders/dispatcher?page=1&tab=completed");
      });
  };

  const handleReject = () => {
    if (!jobCard?.id) return;
    setIsRejectModalOpen(true);
  };

  const pathname = location.pathname.toLowerCase();
  // Determine status from URL tab change logics
  let viewType = "pending";
  if (pathname.includes("rejected")) viewType = "rejected";
  else if (pathname.includes("completed")) viewType = "completed";
  else if (pathname.includes("assigned")) viewType = "assigned";
  else if (pathname.includes("history")) viewType = "history";

  const isCompleted = viewType === "completed";
  const isRejected = viewType === "rejected";
  const isHistory = viewType === "history";

  // Determine association type from URL
  let associationType = "none";
  if (pathname.includes("/consumer/")) associationType = "consumer";
  else if (pathname.includes("/meter/")) associationType = "meter";
  else if (pathname.includes("/facility/")) associationType = "facility";
  else if (pathname.includes("/network/")) associationType = "network";
  else if (pathname.includes("/system/")) associationType = "system";
  else if (pathname.includes("/asset/")) associationType = "asset";

  if (associationType === "none" && jobCard?.sop) {
    const typeDisplay = (
      jobCard.sop.sopAssociationTypeDisplay ||
      jobCard.sop.sop_association_type_display ||
      ""
    ).toLowerCase();
    if (typeDisplay.includes("consumer") || typeDisplay === "consumer")
      associationType = "consumer";
    else if (typeDisplay.includes("meter") || typeDisplay === "meter")
      associationType = "meter";
    else if (typeDisplay.includes("facility") || typeDisplay === "facility")
      associationType = "facility";
    else if (typeDisplay.includes("network") || typeDisplay === "network")
      associationType = "network";
    else if (typeDisplay.includes("system") || typeDisplay === "system")
      associationType = "system";
    else if (typeDisplay.includes("asset") || typeDisplay === "asset")
      associationType = "asset";
  }

  const transformedData = useMemo(
    () => jobCard && wxUtils.transformJobCardDetail(jobCard),
    [jobCard],
  );
  const { jobOverview, consumerDetails, meterDetails } = transformedData || {};
  console.log("consumerDetails", consumerDetails);
  const getAddressStringForConsumer = (addressMap: any) => {
    if (!addressMap) return "N/A";
    const parts = [
      addressMap?.premiseName,
      addressMap?.areaName,
      addressMap?.subAreaName,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "N/A";
  };

  const renderDetailItem = (label: string, value: string | undefined) => (
    <div key={label}>
      <p className="text-xs text-gray-500 font-medium uppercase mb-2">
        {label}
      </p>
      <p className="font-medium text-gray-900">{value || "N/A"}</p>
    </div>
  );

  const getEntityDetails = () => {
    switch (associationType) {
      case "consumer":
        return {
          title: "Consumer Details",
          icon: <User className="w-5 h-5 text-blue-500" />,
          items: [
            { label: "Account No", value: consumerDetails?.accountNo },
            { label: "Name", value: consumerDetails?.consumerName },
            { label: "Email", value: consumerDetails?.email },
            { label: "Phone", value: consumerDetails?.phone },
            {
              label: "Utility Services",
              value: consumerDetails?.consumerMappingData?.utilityService,
            },
            {
              label: "Category",
              value: consumerDetails?.categoryDisplay,
            },
            {
              label: "Sub Category",
              value: consumerDetails?.subCategoryDisplay,
            },
            {
              label: "Service Address",
              value: getAddressStringForConsumer(consumerDetails),
            },
          ],
        };
      case "meter":
        return {
          title: "Meter Details",
          icon: <Gauge className="w-5 h-5 text-blue-500" />,
          items: [
            { label: "Meter No", value: meterDetails?.meterNo },
            { label: "Device No", value: meterDetails?.deviceNo },
            { label: "Type", value: meterDetails?.meterTypeDisplay },
            { label: "Physical Status", value: meterDetails?.physicalStatus },
            {
              label: "Assignment Status",
              value: meterDetails?.assignmentStatus,
            },
            { label: "Reading Status", value: meterDetails?.readingStatus },
            { label: "Premise", value: meterDetails?.premise },
          ],
        };
      case "facility":
        return {
          title: "Facility Details",
          icon: <Building className="w-5 h-5 text-blue-500" />,
          items: [
            {
              label: "Facility ID",
              value: jobCard?.entityInfo?.facilityNo || "N/A",
            },
            {
              label: "Name",
              value: jobCard?.entityInfo?.facilityName || "N/A",
            },
            {
              label: "Type",
              value: jobCard?.entityInfo?.facilityTypeDisplay || "N/A",
            },
            {
              label: "Contact Person",
              value: jobCard?.entityInfo?.contactPersonName || "N/A",
            },
            {
              label: "Contact",
              value: jobCard?.entityInfo?.contactPhone || "N/A",
            },
            {
              label: "Zone/Division Served",
              value: jobCard?.entityInfo?.serviceZoneDisplay || "N/A",
            },
            {
              label: "System",
              value: jobCard?.entityInfo?.systemsCount || "N/A",
            },
            {
              label: "Assets",
              value: jobCard?.entityInfo?.assetsCount || "N/A",
            },
            {
              label: "Location",
              value: jobCard?.entityInfo?.location || "N/A",
            },
          ],
        };
      case "network":
        return {
          title: "Network Details",
          icon: <Wifi className="w-5 h-5 text-blue-500" />,
          items: [
            {
              label: "Network ID",
              value: jobCard?.entityInfo?.networkNo || "N/A",
            },
            { label: "Name", value: jobCard?.entityInfo?.networkName || "N/A" },
            {
              label: "Type",
              value: jobCard?.entityInfo?.networkTypeDisplay || "N/A",
            },
            {
              label: "Contact Person",
              value: jobCard?.entityInfo?.contactPerson || "N/A",
            },
            {
              label: "Contact",
              value: jobCard?.entityInfo?.contactPhone || "N/A",
            },
            {
              label: "Status",
              value: jobCard?.entityInfo?.operationalStatusDisplay || "N/A",
            },
          ],
        };
      case "system":
        return {
          title: "System Details",
          icon: <Server className="w-5 h-5 text-blue-500" />,
          items: [
            {
              label: "System ID",
              value: jobCard?.entityInfo?.systemNo || "N/A",
            },
            { label: "Name", value: jobCard?.entityInfo?.systemName || "N/A" },
            { label: "Type", value: jobCard?.entityInfo?.systemType || "N/A" },
            {
              label: "Contact Person",
              value: jobCard?.entityInfo?.contactPersonName || "N/A",
            },
            {
              label: "Contact",
              value: jobCard?.entityInfo?.contactPhone || "N/A",
            },
            {
              label: "Status",
              value: jobCard?.entityInfo?.statusDisplay || "N/A",
            },
            {
              label: "Location",
              value: jobCard?.entityInfo?.location || "N/A",
            },
          ],
        };
      case "asset":
        return {
          title: "Asset Details",
          icon: <Box className="w-5 h-5 text-blue-500" />,
          items: [
            { label: "Asset ID", value: jobCard?.entityInfo?.assetNo },
            { label: "Name", value: jobCard?.entityInfo?.assetName },
            { label: "Type", value: jobCard?.entityInfo?.assetTypeDisplay },
            {
              label: "Manufacturer",
              value: jobCard?.entityInfo?.manufacturerName,
            },
            {
              label: "Installation Year",
              value: jobCard?.entityInfo?.installationYear,
            },
            {
              label: "Serial Number",
              value: jobCard?.entityInfo?.serialNumber,
            },
            { label: "Status", value: jobCard?.entityInfo?.statusDisplay },
            {
              label: "Facility",
              value: jobCard?.entityInfo?.facilityName || "N/A",
            }, // Facility might not be in transform result yet
            { label: "Location", value: jobCard?.entityInfo?.location },
          ],
        };
      default:
        return null;
    }
  };

  const entityDetails = getEntityDetails();

  return (
    <div className="p-6 bg-white min-h-screen">
      {/* Top Banner / Metrics */}
      {isCompleted || isRejected || isHistory ? (
        <div className="mb-8">
          <h3 className="flex items-center gap-2 font-semibold text-gray-800 mb-4 text-lg">
            <Gauge className="w-5 h-5 text-blue-500" /> Performance Metrics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white border text-sm border-gray-100 rounded-lg p-5 shadow-sm">
              <p className="text-xs text-gray-500 font-medium uppercase mb-4">
                Required field force
              </p>
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-[11px] text-gray-400 mb-1">Estimated</p>
                  <p className="text-sm text-gray-700 font-medium">
                    {jobOverview?.requiredFieldforce || "NA"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-gray-400 mb-1">Actual</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {jobCard?.coworkersInfo?.length || "0"}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border text-sm border-gray-100 rounded-lg p-5 shadow-sm">
              <p className="text-xs text-gray-500 font-medium uppercase mb-4">
                Efforts
              </p>
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-[11px] text-gray-400 mb-1">Estimated</p>
                  <p className="text-sm text-gray-700 font-medium">
                    {jobOverview?.efforts || "NA"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-gray-400 mb-1">Actual</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {jobCard?.actualEfforts || "NA"}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border text-sm border-gray-100 rounded-lg p-5 shadow-sm">
              <p className="text-xs text-gray-500 font-medium uppercase mb-4">
                Downtime
              </p>
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-[11px] text-gray-400 mb-1">Estimated</p>
                  <p className="text-sm text-gray-700 font-medium">
                    {jobOverview?.downtime || "NA"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-gray-400 mb-1">Actual</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {jobOverview?.actualDowntime || "NA"}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border text-sm border-gray-100 rounded-lg p-5 shadow-sm">
              <p className="text-xs text-gray-500 font-medium uppercase mb-4">
                Travel Time
              </p>
              <div className="flex justify-between items-end">
                <div className="text-right w-full">
                  <p className="text-sm font-semibold text-gray-900 w-full text-left">
                    {jobOverview?.travelTime || "NA"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-50/50 border border-gray-100 p-5 rounded-lg shadow-sm">
            <p className="text-xs text-gray-500 font-medium uppercase mb-2">
              Required Field Force
            </p>
            <p className="font-medium text-gray-900">
              {jobOverview?.requiredFieldforce || "Not specified"}
            </p>
          </div>
          <div className="bg-gray-50/50 border border-gray-100 p-5 rounded-lg shadow-sm">
            <p className="text-xs text-gray-500 font-medium uppercase mb-2">
              Estimated Efforts
            </p>
            <p className="font-medium text-gray-900">
              {jobOverview?.efforts || "Not specified"}
            </p>
          </div>
          <div className="bg-gray-50/50 border border-gray-100 p-5 rounded-lg shadow-sm">
            <p className="text-xs text-gray-500 font-medium uppercase mb-2">
              Expected Downtime
            </p>
            <p className="font-medium text-gray-900">
              {jobOverview?.downtime || "None"}
            </p>
          </div>
        </div>
      )}

      {/* Cost Metrics */}
      {isCompleted || isRejected || isHistory ? (
        <div className="mb-8">
          <h3 className="flex items-center gap-2 font-semibold text-gray-800 mb-4 text-lg">
            <DollarSign className="w-5 h-5 text-blue-500" /> Cost Analysis
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white border text-sm border-gray-100 rounded-lg p-5 shadow-sm">
              <p className="text-xs text-gray-500 font-medium uppercase mb-4">
                Labour Cost
              </p>
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-[11px] text-gray-400 mb-1">Estimated</p>
                  <p className="text-sm text-gray-700 font-medium">
                    {jobOverview?.labourCost || "NA"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-gray-400 mb-1">Actual</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {jobOverview?.actualLabourCost || "NA"}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white border text-sm border-gray-100 rounded-lg p-5 shadow-sm">
              <p className="text-xs text-gray-500 font-medium uppercase mb-4">
                Material Cost
              </p>
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-[11px] text-gray-400 mb-1">Estimated</p>
                  <p className="text-sm text-gray-700 font-medium">
                    {jobOverview?.materialCost || "NA"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-gray-400 mb-1">Actual</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {jobOverview?.actualMaterialCost || "NA"}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white border text-sm border-gray-100 rounded-lg p-5 shadow-sm">
              <p className="text-xs text-gray-500 font-medium uppercase mb-4">
                Other Cost
              </p>
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-[11px] text-gray-400 mb-1">Estimated</p>
                  <p className="text-sm text-gray-700 font-medium">
                    {jobOverview?.otherCost || "NA"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-gray-400 mb-1">Actual</p>
                  <p className="text-sm font-semibold text-gray-900">{"NA"}</p>
                </div>
              </div>
            </div>
            <div className="bg-white border text-sm border-gray-100 rounded-lg p-5 shadow-sm">
              <p className="text-xs text-gray-500 font-medium uppercase mb-4">
                Total Cost
              </p>
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-[11px] text-gray-400 mb-1">Estimated</p>
                  <p className="text-sm text-gray-700 font-medium">
                    {jobOverview?.totalEstimatedCost || "NA"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-gray-400 mb-1">Actual</p>
                  <p className="text-sm font-semibold text-blue-600">
                    {jobOverview?.totalCost || "NA"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-8">
          <h3 className="flex items-center gap-2 font-semibold text-gray-800 mb-4 text-lg">
            <DollarSign className="w-5 h-5 text-blue-500" /> Cost Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gray-50/50 border border-gray-100 p-5 rounded-lg shadow-sm">
              <p className="text-xs text-gray-500 font-medium uppercase mb-2">
                Labour Cost
              </p>
              <p className="font-semibold text-gray-900">
                {jobOverview?.labourCost}
              </p>
            </div>
            <div className="bg-gray-50/50 border border-gray-100 p-5 rounded-lg shadow-sm">
              <p className="text-xs text-gray-500 font-medium uppercase mb-2">
                Material Cost
              </p>
              <p className="font-semibold text-gray-900">
                {jobOverview?.materialCost}
              </p>
            </div>
            <div className="bg-gray-50/50 border border-gray-100 p-5 rounded-lg shadow-sm">
              <p className="text-xs text-gray-500 font-medium uppercase mb-2">
                Other Cost
              </p>
              <p className="font-semibold text-gray-900">
                {jobOverview?.otherCost}
              </p>
            </div>
            <div className="bg-gray-50/50 border border-gray-100 p-5 rounded-lg shadow-sm">
              <p className="text-xs text-gray-500 font-medium uppercase mb-2">
                Total Cost
              </p>
              <p className="font-semibold text-gray-900">
                {jobOverview?.totalEstimatedCost}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Entity Details */}
      {entityDetails && (
        <div className="mb-8">
          <h3 className="flex items-center gap-2 font-semibold text-gray-800 mb-4 text-lg">
            {entityDetails.icon} {entityDetails.title}
          </h3>
          <div className="bg-gray-50/50 border border-gray-100 rounded-lg p-6 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-y-8 gap-x-6">
              {entityDetails.items.map((item) =>
                renderDetailItem(item.label, item.value),
              )}
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mb-8">
        <h3 className="font-semibold text-gray-800 mb-3 text-lg">
          Instructions
        </h3>
        <div className="text-[15px] text-gray-600 space-y-2">
          {jobCard?.sop?.documents?.filter((d: any) => d.typeDisplay?.toLowerCase() === "instruction" || d.type === 0)?.length > 0 ? (
            jobCard.sop.documents
              .filter((d: any) => d.typeDisplay?.toLowerCase() === "instruction" || d.type === 0)
              .map((doc: any) => (
                <p key={doc.id}>{doc.description}</p>
              ))
          ) : (
            <p>{jobCard?.additionalData?.notes || "N/A"}</p>
          )}
        </div>
      </div>

      {/* Rejection Details (Rejected Only) */}
      {isRejected && (
        <div className="bg-red-50 border border-red-100 rounded-lg p-6 mb-8 relative">
          <h3 className="flex items-center gap-2 text-red-600 font-semibold mb-6">
            <XCircle className="w-5 h-5" /> Rejection Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            <div>
              <p className="text-[11px] text-gray-500 font-medium uppercase mb-2">
                Rejected By
              </p>
              <p className="text-sm font-medium text-gray-900">
                {jobCard?.rejectedBy}
              </p>
            </div>
            <div>
              <p className="text-[11px] text-gray-500 font-medium uppercase mb-2">
                Rejected On
              </p>
              <p className="text-sm font-medium text-gray-900">
                {jobCard?.rejectedDate}
              </p>
            </div>
            <div>
              <p className="text-[11px] text-gray-500 font-medium uppercase mb-2">
                Reason
              </p>
              <p className="text-[15px] text-gray-900">
                {jobCard?.rejectionReasonDisplay}
              </p>
            </div>
          </div>
          <div className="mb-6">
            <p className="text-[11px] text-gray-500 font-medium uppercase mb-2">
              Detail Comments
            </p>
            <p className="text-[15px] text-gray-900">{jobCard?.remark}</p>
          </div>
          {jobCard?.rejectedProof && (
            <div>
              <p className="text-[11px] text-gray-500 font-medium uppercase mb-3">
                Attached Files
              </p>
              <Button
                variant="outline"
                size="sm"
                className="text-red-500 border-red-200 bg-white hover:bg-red-50 hover:text-red-600 gap-2 font-medium"
                onClick={() => window.open(jobCard.rejectedProof, "_blank")}
              >
                <Paperclip className="w-3.5 h-3.5" /> Attachment
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Co-workers and Customer Signature (Completed Only) */}
      {isCompleted && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-gray-50/50 border border-gray-100 rounded-lg p-6 shadow-sm">
              <h3 className="flex items-center gap-2 font-semibold text-gray-800 mb-6 text-[15px]">
                <Users className="w-4 h-4 text-blue-500" /> Co-workers
              </h3>
              <div className="space-y-4">
                {jobCard?.coworkersInfo?.map((coworker: any, index: number) => (
                  <div
                    key={index}
                    className="flex justify-between items-center text-sm"
                  >
                    <span className="text-gray-700">
                      {coworker.name} ({coworker.role})
                    </span>
                    <span className="text-gray-500 bg-gray-100 px-3 py-1 rounded text-xs font-medium border border-gray-200">
                      {coworker.fieldForceNo}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-gray-100 rounded-lg p-6 shadow-sm">
              <h3 className="flex items-center gap-2 font-semibold text-gray-800 mb-6 text-[15px]">
                <PenTool className="w-4 h-4 text-blue-500" /> Customer Signature
              </h3>
              <div className="border border-dashed border-gray-300 rounded-lg h-32 flex items-center justify-center p-4 bg-gray-50/30 overflow-hidden">
                {(() => {
                  const signature = signatureReportData?.result;
                  const requestUrl = signature?.requestSignature;
                  return requestUrl ? (
                    <img
                      src={requestUrl}
                      alt="Customer Signature"
                      className="max-h-full max-w-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <p className="text-gray-400 font-medium italic">
                      No signature available
                    </p>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <Button
              variant="outline"
              className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600 gap-2"
              onClick={handleReject}
              disabled={acceptJobCard.isPending}
            >
              <XCircle className="w-4 h-4" /> Reject Service Order
            </Button>
            <Button
              className="bg-[#00B050] hover:bg-green-600 text-white gap-2 font-medium"
              onClick={handleApprove}
              disabled={acceptJobCard.isPending}
            >
              <CheckCircle2 className="w-4 h-4" />{" "}
              {acceptJobCard.isPending
                ? "Approving..."
                : "Approve Service Order"}
            </Button>
          </div>
        </>
      )}

      <RejectServiceOrderModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        jobCardId={jobCard?.id?.toString()}
        onSuccess={() =>
          navigate("/service-orders/dispatcher?page=1&tab=completed")
        }
      />
    </div>
  );
};
